import type {ReactElement, ReactNode} from 'react';
import {useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {LoadingStates} from '@Utils/loading.util';
import {MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID} from '../../assistant-message-image-part-actions-row.types';
import {ImageActionId} from '../../image-actions.types';
import styles from '../../assistant-message-image-part-actions-row.module.scss';
import type {ImageActionsOverflowDropdownProps} from './image-actions-overflow-dropdown.types';

const PUBLISHING_ACTION_TO_OPTION: Partial<Record<ImageActionId, PublishingOption>> = {
  [ImageActionId.PUBLISH_TO_SOCIAL_MEDIA]: PublishingOption.PUBLISH_TO_SOCIAL_MEDIA,
  [ImageActionId.PUBLISH_AS_EMAIL_CAMPAIGN]: PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN,
  [ImageActionId.PROMOTE_AS_EVENT]: PublishingOption.PROMOTE_AS_EVENT,
};

const toPromise = (handler: VoidFunction): Promise<void> => {
  handler();

  return Promise.resolve();
};

export function ImageActionsOverflowDropdown(props: ImageActionsOverflowDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const overflowIds = props.overflowActions.map((descriptor): ImageActionId => descriptor.id);

  const has = (id: ImageActionId): boolean => {
    return overflowIds.includes(id);
  };

  const getExcludedPublishingOptions = (): PublishingOption[] => {
    return Object.values(PublishingOption).filter((option): boolean => {
      return !props.overflowActions.some((descriptor): boolean => {
        return PUBLISHING_ACTION_TO_OPTION[descriptor.id] === option;
      });
    });
  };

  const getEditMenuRow = (): ReactNode => {
    if (!has(ImageActionId.OPEN_IN_EDITOR)) {
      return null;
    }

    return props.imageEditMenu.getEditMenuRow();
  };

  const getTurnIntoVideoRow = (): ReactNode => {
    if (!has(ImageActionId.TURN_INTO_VIDEO)) {
      return null;
    }

    return (
      <ControlledListItemType7
        id={'turn-into-video-option'}
        text={window.i18next.t('pmwjs_turn_into_video')}
        icon={'icon-video-ai'}
        onClick={props.handlers[ImageActionId.TURN_INTO_VIDEO]}
        isDisabled={props.isDisabled || props.isGeneratingVideo}
        className={'radius-8'}
      />
    );
  };

  const getDownloadRow = (): ReactNode => {
    if (!has(ImageActionId.DOWNLOAD)) {
      return null;
    }

    const isDownloading = props.loadingStates.downloadLoadingState === LoadingStates.LOADING;

    return (
      <ControlledListItemType7
        id={'download-option'}
        text={window.i18next.t('pmwjs_download')}
        icon={'icon-download'}
        onClick={props.handlers[ImageActionId.DOWNLOAD]}
        isLoading={isDownloading}
        isDisabled={isDownloading}
        className={'radius-8'}
      />
    );
  };

  const getSaveToMyDesignsRow = (): ReactNode => {
    if (!has(ImageActionId.SAVE_TO_MY_DESIGNS)) {
      return null;
    }

    return (
      <ControlledListItemType7
        id={'save-to-my-designs'}
        text={window.i18next.t('pmwjs_save_to_my_designs')}
        icon={'icon-folder-2'}
        onClick={props.handlers[ImageActionId.SAVE_TO_MY_DESIGNS]}
        isLoading={props.loadingStates.saveToMyDesignsLoadingState === LoadingStates.LOADING}
        isDisabled={props.isDisabled}
        className={'radius-8'}
      />
    );
  };

  const getShareDesignRow = (): ReactNode => {
    if (!has(ImageActionId.SHARE_DESIGN)) {
      return null;
    }

    return (
      <ControlledListItemType7
        id={'share-design-option'}
        text={window.i18next.t('pmwjs_share_with_others')}
        icon={'icon-group-add'}
        onClick={props.handlers[ImageActionId.SHARE_DESIGN]}
        isLoading={props.loadingStates.shareLoadingState === LoadingStates.LOADING}
        isDisabled={props.isDisabled}
        className={'radius-8'}
      />
    );
  };

  const getPopup = (): ReactElement => {
    if (props.imageEditMenu.isEditMenuInModalMode && props.imageEditMenu.isEditSubmenuScreen) {
      return props.imageEditMenu.getEditSubmenu();
    }

    return (
      <>
        {getEditMenuRow()}
        {getTurnIntoVideoRow()}
        {getDownloadRow()}
        <PublishingOptions
          chatId={props.chatId}
          hashedId={props.hashedId}
          loadingStates={props.loadingStates}
          publishToSocialMedia={(): Promise<void> => {
            return toPromise(props.handlers[ImageActionId.PUBLISH_TO_SOCIAL_MEDIA]);
          }}
          publishAsEmailCampaign={(): Promise<void> => {
            return toPromise(props.handlers[ImageActionId.PUBLISH_AS_EMAIL_CAMPAIGN]);
          }}
          promoteAsEvent={(): Promise<void> => {
            return toPromise(props.handlers[ImageActionId.PROMOTE_AS_EVENT]);
          }}
          removeLastItemBottomMargin={false}
          excludedOptions={getExcludedPublishingOptions()}
        />
        {getSaveToMyDesignsRow()}
        {getShareDesignRow()}
      </>
    );
  };

  useUpdateMoreOptionsModal(
    getPopup(),
    [
      props.isDisabled,
      props.imageEditMenu.editScreen,
      props.loadingStates.downloadLoadingState,
      props.loadingStates.shareLoadingState,
      props.loadingStates.saveToMyDesignsLoadingState,
      props.loadingStates.makeTextEditableLoadingState,
      props.loadingStates.loadingPublishingOption,
    ],
    {panelIdOpenedInModal: MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID, additionalPanelProps: props.imageEditMenu.getMoreOptionsModalHeaderProps()}
  );

  return (
    <Dropdown
      open={isOpen}
      onOpen={(): void => {
        setIsOpen(true);
      }}
      onClose={(): void => {
        setIsOpen(false);
      }}
      isSmallScreenModalBottomSheet
      doCloseOnInnerClick={true}
      showModalForSmallScreen
      modalTitle={window.i18next.t('pmwjs_more_options')}
      panelIdForModal={MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID}
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      popupClassName={styles.actionPopup}
      className={styles.positionUnset}
      selector={
        <Button
          disabled={!props.canWrite}
          customClasses={`radius-8 _full-height ${styles.moreOptionsButton} `}
          type={Type.SECONDARY}
          size={Size.XSMALL}
          icon={'icon-more-h'}
          isActive={isOpen}
        />
      }
      popup={getPopup()}
    />
  );
}
